// icons.jsx — minimal stroke icon set (Lucide-inspired, original)
import React from 'react';

const Ic = {};
// JSDoc-typed so TS callers importing this .jsx module can pass standard SVG
// props (style, aria-hidden, etc.) through to the underlying <svg> — allowJs
// infers the exported member types from here even with checkJs off.
const make = (path) =>
  /** @param {React.SVGProps<SVGSVGElement>} [props] */
  (props = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {path}
    </svg>
  );

Ic.dashboard = make(
  <>
    <rect x="3" y="3" width="7" height="9" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" />
  </>
);
Ic.catalog = make(
  <>
    <path d="M4 5h12a2 2 0 012 2v13H6a2 2 0 01-2-2V5z" />
    <path d="M4 5a2 2 0 012-2h12" />
    <path d="M8 8h8M8 12h6" />
  </>
);
Ic.commercial = make(
  <>
    <path d="M3 7h18l-2 12H5L3 7z" />
    <path d="M8 7V5a4 4 0 018 0v2" />
  </>
);
Ic.finance = make(
  <>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M3 10h18" />
    <path d="M7 15h4" />
  </>
);
Ic.contract = make(
  <>
    <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
    <path d="M14 3v6h6" />
    <path d="M8 13h8M8 17h5" />
  </>
);
Ic.materials = make(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="9" cy="11" r="2" />
    <path d="M21 17l-5-5-7 7" />
  </>
);
Ic.news = make(
  <>
    <path d="M4 4h12v16H6a2 2 0 01-2-2V4z" />
    <path d="M16 8h3a1 1 0 011 1v9a2 2 0 01-2 2" />
    <path d="M7 8h6M7 12h6M7 16h4" />
  </>
);
Ic.search = make(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </>
);
Ic.bell = make(
  <>
    <path d="M6 8a6 6 0 0112 0c0 7 3 8 3 8H3s3-1 3-8z" />
    <path d="M10 21a2 2 0 004 0" />
  </>
);
Ic.help = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 4" />
    <circle cx="12" cy="17" r=".6" fill="currentColor" />
  </>
);
// Krug sa slovom „i", nacrtan na istoj mreži kao ostale ikone: znak i njegov krug su u jednom
// liku, pa se centriraju geometrijski. Slovo kao tekst u dugmetu nikad ne sedne u sredinu, jer
// se poravnava po osnovnoj liniji, a ne po sredini svoje kućice.
Ic.info = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <circle cx="12" cy="7.8" r=".6" fill="currentColor" />
  </>
);
Ic.chev = make(<path d="M9 6l6 6-6 6" />);
Ic.chevDown = make(<path d="M6 9l6 6 6-6" />);
Ic.plus = make(
  <>
    <path d="M12 5v14M5 12h14" />
  </>
);
Ic.download = make(
  <>
    <path d="M12 4v12M6 10l6 6 6-6" />
    <path d="M4 20h16" />
  </>
);
Ic.upload = make(
  <>
    <path d="M12 20V8M6 14l6-6 6 6" />
    <path d="M4 4h16" />
  </>
);
Ic.filter = make(<path d="M4 5h16l-6 8v6l-4-2v-4z" />);
// Sort: two arrows, one up one down. The filter funnel next to it says "narrow";
// this one has to say "reorder", and an arrow pair is the sign people already read that way.
Ic.sort = make(
  <>
    <path d="M7 4v16M4 17l3 3 3-3" />
    <path d="M17 20V4M14 7l3-3 3 3" />
  </>,
);
Ic.grid = make(
  <>
    <rect x="4" y="4" width="7" height="7" rx="1" />
    <rect x="13" y="4" width="7" height="7" rx="1" />
    <rect x="4" y="13" width="7" height="7" rx="1" />
    <rect x="13" y="13" width="7" height="7" rx="1" />
  </>
);
Ic.list = make(
  <>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </>
);
Ic.arrowUp = make(<path d="M12 19V5M5 12l7-7 7 7" />);
Ic.arrowDown = make(<path d="M12 5v14M5 12l7 7 7-7" />);
// Disclosure chevron: thinner than arrowDown on purpose. An arrow says "go there",
// a chevron says "this opens" — the sidebar group headers need the second.
Ic.chevron = make(<path d="M8 10l4 4 4-4" />);
Ic.check = make(<path d="M5 12l5 5L20 7" />);
Ic.x = make(<path d="M6 6l12 12M18 6L6 18" />);
Ic.eye = make(
  <>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </>
);
Ic.dl = Ic.download;
Ic.print = make(
  <>
    <path d="M6 9V3h12v6" />
    <rect x="4" y="9" width="16" height="8" rx="1" />
    <path d="M7 17v4h10v-4" />
  </>
);
Ic.copy = make(
  <>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 8V5a1 1 0 00-1-1H5a1 1 0 00-1 1v10a1 1 0 001 1h3" />
  </>
);
Ic.send = make(<path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />);
Ic.play = make(<path d="M8 5l12 7-12 7V5z" fill="currentColor" />);
Ic.pdf = make(
  <>
    <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
    <path d="M14 3v6h6" />
  </>
);
Ic.image = make(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="9" cy="11" r="2" />
    <path d="M21 17l-5-5-7 7" />
  </>
);
Ic.zip = make(
  <>
    <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
    <path d="M10 4v3M10 9v2M10 13v2M10 17v2" />
  </>
);
Ic.brand = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3a9 9 0 010 18M3 12h18" />
  </>
);
Ic.world = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a13 13 0 010 18M12 3a13 13 0 000 18" />
  </>
);
Ic.calendar = make(
  <>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 3v4M16 3v4" />
  </>
);
Ic.user = make(
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0116 0" />
  </>
);
Ic.factory = make(
  <>
    <path d="M3 21V10l5 3V10l5 3V8l8 5v8H3z" />
    <path d="M8 21v-4M13 21v-4M18 21v-4" />
  </>
);
Ic.fire = make(
  <>
    <path d="M12 3s5 4 5 9a5 5 0 01-10 0c0-2 1-3 1-3s2 1 2 3c0-3 2-5 2-9z" />
  </>
);
Ic.drop = make(
  <>
    <path d="M12 3s7 7 7 12a7 7 0 01-14 0c0-5 7-12 7-12z" />
  </>
);
Ic.bolt = make(<path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />);
Ic.tools = make(
  <>
    <path d="M14 6l4 4-9 9-4 1 1-4 9-9z" />
    <path d="M16 8l4-4-2-2-4 4" />
  </>
);
Ic.book = make(
  <>
    <path d="M4 5a2 2 0 012-2h13v18H6a2 2 0 01-2-2V5z" />
    <path d="M19 16H6a2 2 0 00-2 2" />
  </>
);
Ic.cog = make(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
  </>
);
Ic.menu = make(<path d="M4 6h16M4 12h16M4 18h16" />);
Ic.arrowRight = make(<path d="M5 12h14M13 5l7 7-7 7" />);
Ic.refresh = make(
  <>
    <path d="M21 12a9 9 0 11-3-6.7" />
    <path d="M21 4v5h-5" />
  </>
);
Ic.alert = make(
  <>
    <path d="M12 3l10 18H2L12 3z" />
    <path d="M12 10v5" />
    <circle cx="12" cy="18" r=".6" fill="currentColor" />
  </>
);
Ic.trend = make(
  <>
    <path d="M3 17l6-6 4 4 8-8" />
    <path d="M14 7h7v7" />
  </>
);
Ic.box = make(
  <>
    <path d="M3 7l9-4 9 4v10l-9 4-9-4V7z" />
    <path d="M3 7l9 4 9-4M12 11v10" />
  </>
);
Ic.truck = make(
  <>
    <rect x="2" y="7" width="12" height="9" rx="1" />
    <path d="M14 10h4l3 3v3h-7" />
    <circle cx="6" cy="18" r="2" />
    <circle cx="17" cy="18" r="2" />
  </>
);
Ic.handshake = make(
  <>
    <path d="M2 12l4-4 5 4-3 3-3-1-3 2z" />
    <path d="M22 12l-4-4-5 4 3 3 3-1 3 2z" />
    <path d="M11 12l1 1 2-2" />
  </>
);
Ic.chat = make(
  <>
    <path d="M21 11.5a8.5 8.5 0 0 1-12.5 7.5L3 21l2-5.5A8.5 8.5 0 1 1 21 11.5z" />
    <path d="M8 11h8M8 14.5h5" />
  </>
);
// Threads: a message's bubble, and a reply's smaller one over it.
Ic.threads = make(
  <>
    <path d="M15 8V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h1v3l3-3" />
    <path d="M11 10h8a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-1v2.5L15.5 19H11a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2z" />
  </>
);
// Chat: channel mark, private channel, mention, emoji, edit, delete, members, leave, browse, back.
Ic.hash = make(<path d="M5 9h15M4 15h15M10 3 8 21M16 3l-2 18" />);
Ic.lock = make(
  <>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </>
);
Ic.at = make(
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" />
  </>
);
Ic.smile = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14a4 4 0 0 0 7 0" />
    <path d="M9 9.5h.01M15 9.5h.01" />
  </>
);
Ic.pencil = make(
  <>
    <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4z" />
    <path d="M13.5 6.5l4 4" />
  </>
);
Ic.trash = make(
  <>
    <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
    <path d="M10 11v6M14 11v6" />
  </>
);
Ic.users = make(
  <>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
    <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18.5 14.2A6.5 6.5 0 0 1 21.5 20" />
  </>
);
Ic.leave = make(
  <>
    <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
    <path d="M10 16l-4-4 4-4M6 12h10" />
  </>
);
Ic.compass = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M15.5 8.5l-2 5-5 2 2-5z" />
  </>
);
Ic.arrowLeft = make(<path d="M19 12H5M11 6l-6 6 6 6" />);
// Chat writing box: formatting and files.
Ic.bold = make(<path d="M7 5h6a3.5 3.5 0 0 1 0 7H7zM7 12h7a3.5 3.5 0 0 1 0 7H7z" strokeWidth="2.2" />);
Ic.italic = make(<path d="M10 5h8M6 19h8M14 5l-4 14" />);
Ic.strike = make(
  <>
    <path d="M4 12h16" />
    <path d="M16.5 7.5C16 6 14.5 5 12 5c-3 0-4.5 1.5-4.5 3.2 0 1.3.8 2.3 2.5 3M7.5 16.5C8 18 9.8 19 12 19c3 0 4.5-1.4 4.5-3.2 0-.6-.2-1.2-.5-1.8" />
  </>
);
Ic.link = make(
  <>
    <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" />
    <path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" />
  </>
);
Ic.listBullet = make(
  <>
    <path d="M9 6h11M9 12h11M9 18h11" />
    <path d="M4.5 6h.01M4.5 12h.01M4.5 18h.01" strokeWidth="2.6" />
  </>
);
Ic.listNumbered = make(
  <>
    <path d="M10 6h10M10 12h10M10 18h10" />
    <path d="M4 5l1.5-1v5M3.5 13.5a1.5 1.5 0 0 1 3 0c0 1-3 2-3 3.5h3M3.5 19.2h1.8a1 1 0 0 1 0 2H3.5" strokeWidth="1.4" />
  </>
);
Ic.quote = make(
  <>
    <path d="M4 5v14" strokeWidth="2.2" />
    <path d="M9 8h11M9 12h11M9 16h7" />
  </>
);
Ic.code = make(<path d="M9 7l-5 5 5 5M15 7l5 5-5 5" />);
Ic.codeBlock = make(
  <>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M10 9.5L7.5 12l2.5 2.5M14 9.5l2.5 2.5-2.5 2.5" />
  </>
);
Ic.attach = make(<path d="M20 11.5l-7.8 7.8a5 5 0 0 1-7-7l7.8-7.8a3.3 3.3 0 0 1 4.7 4.7l-7.7 7.7a1.7 1.7 0 0 1-2.4-2.4l7.1-7.1" />);

export default Ic;
